import React, { useEffect, useState } from 'react';
import { useGlobal } from '../../global/GlobalContext';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiChevronRight,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiPackage,
  FiMessageCircle,
} from 'react-icons/fi';
import { BiLogoWhatsapp } from 'react-icons/bi';

const statusVisual = (status) => {
  if (status === 'Order Cancelled ' || status === 'Payment Failed') {
    return { bg: 'bg-gray-500', Icon: FiXCircle };
  }
  if (status === 'Pending') {
    return { bg: 'bg-amber-500', Icon: FiClock };
  }
  return { bg: 'bg-green-600', Icon: FiCheckCircle };
};

const OrderCardSkeleton = () => (
  <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm mb-4 animate-pulse">
    <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
      <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
      <div className="flex-1">
        <div className="h-3.5 bg-gray-200 rounded w-1/3 mb-2" />
        <div className="h-3 bg-gray-100 rounded w-1/2" />
      </div>
    </div>
    <div className="mt-4 h-16 bg-gray-100 rounded-xl" />
  </div>
);

const MyOrders = () => {
  const { token } = useGlobal();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_my_orders`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data.status) {
          setOrders(response.data.orders);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token]);

  const cancelOrderHandler = async () => {
    if (!orderToCancel) return;
    setActionLoading(true);
    try {
      await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/order_cancel_by_customer/${orderToCancel}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderToCancel ? { ...order, shipping_status: 'Order Cancelled ' } : order
        )
      );
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoading(false);
      setShowWarning(false);
    }
  };

  return (
    <>
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <>
            <OrderCardSkeleton />
            <OrderCardSkeleton />
            <OrderCardSkeleton />
          </>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
              <FiPackage size={26} className="text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-900">No orders yet</h3>
            <p className="text-sm text-gray-500 max-w-xs">
              When you place an order, it will show up here so you can track and manage it.
            </p>
            <Link
              to="/"
              className="mt-2 inline-block px-5 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-medium"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          orders.map((order) => {
            const { bg, Icon } = statusVisual(order.shipping_status);
            const canCancel =
              order.shipping_status !== 'Order Cancelled '
              && order.shipping_status !== 'Delivered'
              && order.shipping_status !== 'Refund Received'
              && (() => {
                const orderDate = new Date(order.order_date);
                const now = new Date();
                const hoursPassed = (now - orderDate) / (1000 * 60 * 60);
                return hoursPassed <= 24;
              })();

            return (
              <div key={order.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm mb-4">
                <button
                  onClick={() => navigate(`/myaccount/order/${order.id}`)}
                  className="flex items-center justify-between w-full pb-4 border-b border-gray-100"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className={`w-10 h-10 ${bg} text-white rounded-full flex items-center justify-center shrink-0`}>
                      <Icon size={18} />
                    </span>
                    <div>
                      <p className="text-gray-900 font-medium text-sm">{order.shipping_status?.trim()}</p>
                      <p className="text-xs text-gray-500">Placed on {order.order_date}</p>
                    </div>
                  </div>
                  <FiChevronRight className="text-gray-300 shrink-0" size={22} />
                </button>

                <div className="mt-4 space-y-2">
                  {order.order_items.map((item) => (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-xl border border-gray-100 text-sm">
                      <p className="text-gray-800 font-medium">{item.product_name}</p>
                      <div className="flex justify-between text-gray-500 text-xs mt-1">
                        <span>Qty: {item.quantity} &times; ₹{item.price}</span>
                        <span className="font-medium text-gray-700">₹{item.total_price}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-sm text-gray-700 font-medium mt-3">
                  Net Amount: ₹{order.net_amt}
                </p>

                {order.shipping_status === 'Delivered' ? (
                  <button
                    onClick={() => navigate(`/myaccount/return/${order.id}`)}
                    className="mt-3 border border-green-500 text-green-600 text-xs px-3 py-1.5 rounded-lg font-medium"
                  >
                    Return
                  </button>
                ) : (
                  canCancel && (
                    <>
                      <p className="text-xs text-gray-500 mt-3">
                        Heads up — the cancel option is only available within 24 hours of placing an order.
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <button
                          onClick={() => { setShowWarning(true); setOrderToCancel(order.id); }}
                          className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg font-medium"
                        >
                          Cancel Order
                        </button>
                        <Link
                          to={'https://wa.me/+919733228888?text=Hello%20MPI%20Shop%2C%20I%20need%20assistance%20regarding%20my%20order%20with%20order%20ID%20' + order.id}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-gray-500"
                        >
                          <BiLogoWhatsapp className="text-green-500" size={20} />
                        </Link>
                      </div>
                    </>
                  )
                )}
              </div>
            );
          })
        )}
      </div>

      {showWarning && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white p-6 rounded-2xl shadow-lg max-w-sm w-full">
            <div className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center mb-3">
              <FiMessageCircle className="text-red-500" size={20} />
            </div>
            <p className="text-sm text-gray-600">
              Frequent cancellations can lead to account restrictions, so let's try to avoid that where possible.
              If you have any concerns, feel free to reach out to us on WhatsApp — we're happy to help.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setShowWarning(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium"
              >
                No
              </button>
              <button
                onClick={cancelOrderHandler}
                className="px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium disabled:opacity-60"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Cancelling...
                  </span>
                ) : (
                  'Yes, Cancel'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyOrders;
